/**
 * Support hours: Mon–Fri, 8am–5pm America/New_York. Mirrors the schedule()
 * logic in the approved design (Carlos, 10/7 — "yes the hours in the design
 * are correct... see how the messages change based on time of day? Implement
 * that too"), used by both the "Talk to a person?" confirm sheet and the
 * post-escalation handoff banner.
 *
 * Takes `now` as a parameter (defaulting to the real clock) so callers and
 * tests can pin an exact moment rather than depending on the host's local
 * timezone — Intl.DateTimeFormat's explicit `timeZone` already makes the
 * actual open/closed computation itself immune to the host's zone.
 */
export const SUPPORT_HOURS_LABEL = 'Mon–Fri, 8am–5pm ET';

export interface SupportHoursState {
  open: boolean;
  /** e.g. "open now" or "closed now, opens tomorrow at 8am ET". */
  openState: string;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function supportHoursState(now: Date = new Date()): SupportHoursState {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    weekday: 'short',
    hour: 'numeric',
    hour12: false,
  }).formatToParts(now);
  const map: Record<string, string> = {};
  parts.forEach((p) => {
    map[p.type] = p.value;
  });
  const day = WEEKDAYS.indexOf(map.weekday);
  // hour12:false can report midnight as "24" in some engines/ICU versions —
  // normalize to 0 the same way the design's own schedule() does.
  const hour = parseInt(map.hour, 10) % 24;

  const weekday = day >= 1 && day <= 5;
  const open = weekday && hour >= 8 && hour < 17;
  if (open) return { open: true, openState: 'open now' };

  let when: string;
  if (weekday && hour < 8) {
    when = 'today at 8am ET';
  } else if (day >= 1 && day <= 4) {
    // Mon-Thu evening (5pm or later) — next open is tomorrow morning.
    when = 'tomorrow at 8am ET';
  } else {
    // Fri evening, or any time Sat/Sun — next open is Monday morning.
    when = 'Monday at 8am ET';
  }
  return { open: false, openState: `closed now, opens ${when}` };
}
